import React, { useState } from 'react';
import { usePolicy } from '../context/PolicyContext';
import './Form.css';

const RenewPolicy: React.FC = () => {
  const { getPolicy, renewPolicy } = usePolicy();
  const [policyNumber, setPolicyNumber] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [newPremium, setNewPremium] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [policyFound, setPolicyFound] = useState(false);
  const [policyDetails, setPolicyDetails] = useState<any>(null);

  const handlePolicyCheck = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setPolicyFound(false);
    setPolicyDetails(null);

    if (!policyNumber.trim()) {
      setErrors({ policyNumber: 'Please enter a policy number' });
      return;
    }

    const policy = getPolicy(policyNumber.trim());
    if (policy) {
      if (policy.status === 'cancelled') {
        setErrors({ policyNumber: 'Cancelled policies cannot be renewed. Please reinstate first.' });
        return;
      }
      setPolicyFound(true);
      setPolicyDetails(policy);
      // Pre-fill with current end date + 1 year
      const currentEndDate = new Date(policy.endDate);
      currentEndDate.setFullYear(currentEndDate.getFullYear() + 1);
      setNewEndDate(currentEndDate.toISOString().split('T')[0]);
      setNewPremium(policy.premiumAmount.toString());
    } else {
      setErrors({ policyNumber: 'Policy not found' });
    }
  };

  const handleRenew = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!newEndDate) {
      newErrors.newEndDate = 'New end date is required';
    } else if (policyDetails && new Date(newEndDate) <= new Date(policyDetails.endDate)) {
      newErrors.newEndDate = 'New end date must be after current end date';
    }

    if (!newPremium || parseFloat(newPremium) <= 0) {
      newErrors.newPremium = 'Premium amount must be greater than 0';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    renewPolicy(policyNumber.trim(), newEndDate, parseFloat(newPremium));
    setSuccess(true);
    setPolicyNumber('');
    setNewEndDate('');
    setNewPremium('');
    setPolicyFound(false);
    setPolicyDetails(null);

    setTimeout(() => setSuccess(false), 5000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="form-container">
      <h2>Renew Insurance Policy</h2>

      {success && (
        <div className="success-message">
          Policy has been renewed successfully!
        </div>
      )}

      {!policyFound ? (
        <form onSubmit={handlePolicyCheck} className="form">
          <div className="form-group">
            <label htmlFor="policyNumber">Policy Number *</label>
            <input
              type="text"
              id="policyNumber"
              name="policyNumber"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
              placeholder="Enter policy number"
              className={errors.policyNumber ? 'error' : ''}
            />
            {errors.policyNumber && <span className="error-text">{errors.policyNumber}</span>}
          </div>

          <button type="submit" className="submit-button">Verify Policy</button>
        </form>
      ) : (
        <div>
          {policyDetails && (
            <div className="policy-preview">
              <h3>Current Policy Information</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <strong>Policy Number:</strong>
                  <span>{policyDetails.policyNumber}</span>
                </div>
                <div className="detail-item">
                  <strong>Policy Holder:</strong>
                  <span>{policyDetails.policyHolderName}</span>
                </div>
                <div className="detail-item">
                  <strong>Current End Date:</strong>
                  <span>{formatDate(policyDetails.endDate)}</span>
                </div>
                <div className="detail-item">
                  <strong>Current Premium:</strong>
                  <span>${policyDetails.premiumAmount.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleRenew} className="form">
            <div className="form-group">
              <label htmlFor="policyNumber">Policy Number</label>
              <input
                type="text"
                id="policyNumber"
                name="policyNumber"
                value={policyNumber}
                disabled
                className="disabled-input"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="newEndDate">New End Date *</label>
                <input
                  type="date"
                  id="newEndDate"
                  name="newEndDate"
                  value={newEndDate}
                  onChange={(e) => {
                    setNewEndDate(e.target.value);
                    if (errors.newEndDate) {
                      setErrors({ ...errors, newEndDate: '' });
                    }
                  }}
                  min={policyDetails?.endDate}
                  className={errors.newEndDate ? 'error' : ''}
                />
                {errors.newEndDate && <span className="error-text">{errors.newEndDate}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="newPremium">New Premium Amount ($) *</label>
                <input
                  type="number"
                  id="newPremium"
                  name="newPremium"
                  value={newPremium}
                  onChange={(e) => {
                    setNewPremium(e.target.value);
                    if (errors.newPremium) {
                      setErrors({ ...errors, newPremium: '' });
                    }
                  }}
                  min="0"
                  step="0.01"
                  className={errors.newPremium ? 'error' : ''}
                />
                {errors.newPremium && <span className="error-text">{errors.newPremium}</span>}
              </div>
            </div>

            <div className="button-group">
              <button type="submit" className="submit-button">Confirm Renewal</button>
              <button
                type="button"
                className="submit-button secondary"
                onClick={() => {
                  setPolicyFound(false);
                  setPolicyNumber('');
                  setNewEndDate('');
                  setNewPremium('');
                  setPolicyDetails(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default RenewPolicy;


